import React, { useState } from 'react'
import { Task } from '../../lib/types/database.types'
import { TASK_PRIORITY_COLORS, TASK_PRIORITY_LABELS } from '../../lib/config/project-config'
import { formatDate, formatDateTime } from '../../utils/formatters'
import { TaskInfoPanel } from './TaskInfoPanel'

type TaskViewDialogProps = {
  task: Task
  onClose: () => void
  assigneeLabel?: string
  createdByLabel?: string
  parentTaskTitle?: string
  headerTitle?: string
  headerSubtitle?: string
  fullScreen?: boolean
  onAddSubTask?: () => void
  children?: React.ReactNode
}

export const TaskViewDialog: React.FC<TaskViewDialogProps> = ({
  task,
  onClose,
  assigneeLabel,
  createdByLabel,
  parentTaskTitle,
  headerTitle = 'Task Details',
  headerSubtitle = 'Review task details and current delivery status.',
  fullScreen = false,
  onAddSubTask,
  children,
}) => {
  const [copied, setCopied] = useState(false)
  const taskUrl = task.unique_task_id && typeof window !== 'undefined'
    ? `${window.location.origin}/tasks/${encodeURIComponent(task.unique_task_id)}`
    : ''

  const handleCopyTaskUrl = async () => {
    if (!taskUrl || !navigator.clipboard) return
    await navigator.clipboard.writeText(taskUrl)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <div
        className={
          fullScreen
            ? 'flex h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-none flex-col overflow-hidden rounded-xl border border-stone-300 bg-stone-100 shadow-xl'
            : 'flex max-h-[calc(100vh-2rem)] w-full max-w-[84rem] flex-col overflow-hidden rounded-xl border border-stone-300 bg-stone-100 shadow-xl'
        }
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{headerTitle}</h2>
            <p className="text-sm text-gray-500">{headerSubtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            {onAddSubTask && !task.parent_task_id && task.type === 'new_feature' && (
              <button
                type="button"
                className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
                onClick={onAddSubTask}
              >
                Add Sub-task
              </button>
            )}
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleCopyTaskUrl}
              disabled={!taskUrl}
            >
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <button type="button" className="text-gray-500 hover:text-gray-700" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <div className={fullScreen ? 'grid gap-6 xl:grid-cols-2' : 'space-y-4'}>
            <TaskInfoPanel
              task={task}
              assigneeLabel={assigneeLabel}
            />

            <section className="rounded-xl border border-stone-300 bg-stone-50 p-5">
              <div className="mb-4">
                <h3 className="text-base font-semibold text-gray-900">Additional Information</h3>
                <p className="text-sm text-gray-500">Extended delivery and release details</p>
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                <div className="md:col-span-2">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Task URL</p>
                  {taskUrl ? (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <a className="break-all text-sm text-blue-700 underline" href={taskUrl}>
                        {taskUrl}
                      </a>
                      <button
                        type="button"
                        className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                        onClick={handleCopyTaskUrl}
                      >
                        {copied ? 'Copied' : 'Copy link'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-800">Not available</p>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Added By</p>
                  <p className="text-sm text-gray-800">{createdByLabel || 'Unknown user'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Sales Priority</p>
                  <span className="inline-flex rounded-full border px-2.5 py-1 text-xs font-medium" style={{ borderColor: TASK_PRIORITY_COLORS[task.client_priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280', color: TASK_PRIORITY_COLORS[task.client_priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280' }}>
                    {TASK_PRIORITY_LABELS[task.client_priority as keyof typeof TASK_PRIORITY_LABELS] || task.client_priority}
                  </span>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Dev Priority</p>
                  <span className="inline-flex rounded-full border px-2.5 py-1 text-xs font-medium" style={{ borderColor: TASK_PRIORITY_COLORS[task.priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280', color: TASK_PRIORITY_COLORS[task.priority as keyof typeof TASK_PRIORITY_COLORS] || '#6b7280' }}>
                    {TASK_PRIORITY_LABELS[task.priority as keyof typeof TASK_PRIORITY_LABELS] || task.priority}
                  </span>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Weight</p>
                  <p className="text-sm text-gray-800">{task.weight ?? 'Not set'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Billable</p>
                  <p className="text-sm text-gray-800">{task.billable ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Estimated Hours</p>
                  <p className="text-sm text-gray-800">{task.estimated_hours ? `${task.estimated_hours}h` : 'Not set'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Release Date</p>
                  <p className="text-sm text-gray-800">{task.release_date ? formatDate(task.release_date) : 'Not set'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Release Version</p>
                  <p className="text-sm text-gray-800">{task.release_version || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Completed At</p>
                  <p className="text-sm text-gray-800">{task.completed_at ? formatDateTime(task.completed_at) : 'Not completed yet'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Created</p>
                  <p className="text-sm text-gray-800">{formatDateTime(task.created_at)}</p>
                </div>
                {task.parent_task_id && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Parent Task</p>
                    <p className="text-sm text-gray-800">{parentTaskTitle || 'Unknown task'}</p>
                  </div>
                )}
              </div>
            </section>

            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
